/**
 * Billing and subscription service utilities
 */
import api, { getErrorMessage } from './apiClient';

/**
 * Create a Stripe Checkout session and return the checkout URL
 * @param {string} priceId - Optional Stripe price ID, defaults to pro_monthly
 * @param {string} returnUrl - Optional return URL after checkout
 * @returns {Promise<string>} Checkout URL to redirect user to
 */
export async function createCheckoutSession(priceId = null, returnUrl = null) {
  const payload = {};
  if (priceId) payload.price_id = priceId;
  if (returnUrl) payload.return_url = returnUrl;

  const response = await api.post('/api/billing/checkout/start/', payload);
  return response.data.checkout_url;
}

/**
 * Get Stripe Billing Portal URL for subscription management
 * @param {string} returnUrl - Optional return URL after portal
 * @returns {Promise<string>} Portal URL to redirect user to
 */
export async function getBillingPortalUrl(returnUrl = null) {
  const params = returnUrl ? { return_url: returnUrl } : {};
  const response = await api.get('/api/billing/portal/', { params });
  return response.data.portal_url;
}

/**
 * Fetch user's subscription entitlements and quotas
 * @returns {Promise<Object>} Entitlements object with plan, status, and quotas
 */
export async function fetchEntitlements() {
  const response = await api.get('/api/billing/entitlements/');
  return response.data;
}

/**
 * Open Stripe Checkout in current window
 * @param {string} priceId - Optional Stripe price ID
 * @param {string} returnUrl - Optional return URL
 * @returns {Promise<void>}
 */
export async function startCheckout(priceId = null, returnUrl = null) {
  try {
    const checkoutUrl = await createCheckoutSession(priceId, returnUrl);
    window.location.href = checkoutUrl;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}

/**
 * Open Stripe Billing Portal in current window
 * @param {string} returnUrl - Optional return URL
 * @returns {Promise<void>}
 */
export async function openBillingPortal(returnUrl = null) {
  try {
    const portalUrl = await getBillingPortalUrl(returnUrl);
    window.location.href = portalUrl;
  } catch (error) {
    throw new Error(getErrorMessage(error));
  }
}
